import { Card, Col, Container, CostGuideBanner, HeadingTwo, maxWidth, Row, SmallText } from 'hui';
import PropTypes from 'prop-types';
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import ActionTypes from '../../../constants/ActionTypes';
import {
  getArticleByKey,
  getArticleStatusByKey,
  getCategoryDetails,
  getCatgoryByArticleKey,
  getLatestTopicArticlesByArticleKey,
  getModalQuestionsByCategory,
  getSecondaryTopicsByArticleKey,
  getStates,
  getTopicByArticleKey,
  getTopicCatgoryByArticleKey
} from '../../../selectors';
import { generateArticleSchema } from '../../../utils';
import { trackEvent } from '../../../utils/googleAnalytics';
import Breadcrumbs from '../Breadcrumbs';
import HeroJumbotron from '../HeroJumbotronContainer';
import {
  addEnglishArticleWord,
  formatDate,
  getArticleBreadcrumbs,
  googleTagManager
} from '../utils';
import ArticleBody from './ArticleBody';
import ArticleSuggestions from './ArticleSuggestions';
import CategoryList from './CategoryList';
import GetQuotesWidget from './GetQuotesWidgetContainer';
import LatestArticles from './LatestArticles';
import SocialShareBar from './SocialShareBar';
import SocialShareBarMobile from './SocialShareBarMobile';
import TopicLabels from './TopicLabels';
import Video from './Video';
import loadable from '@loadable/component';
import { useSetupSeoMetrics } from '../../../utils/snowplow';

const FAQ = loadable(() => import('../../../components/FAQ'));
const ArticleNotFound = loadable(() => import('./ArticleNotFound'));
const ArticleError = loadable(() => import('./ArticleError'));

const GetQuotesModal = lazy(() =>
  import(/* webpackPrefetch: true */ '../../../packages/GetQuotesModal/Index')
);

const HrContainer = styled.hr`
  border: 1px dotted #c6c6c7;
  margin: 30px 0;
`;

const ResponsiveCard = styled(Card)`
  ${maxWidth('md')} {
    border-radius: 0;
  }
`;

const StyledCol = styled(Col)`
  width: 4rem;
  max-width: 4rem;
`;

const goToPage = (location, key) => {
  const param = location ? `?w=${location}` : '';
  const url = key ? `/find/${key}/get_quotes_simple${param}` : `/get_quotes_simple${param}`;
  window.location.href = url;
};

const getCookieLocation = string => {
  if (typeof string !== 'string') {
    return;
  }
  const splitAtLocation = string.split('landing_postcode=');
  if (splitAtLocation.length > 1) {
    return splitAtLocation[1].split(';')[0];
  }
  return;
};

const Article = ({
  articleStatus,
  article,
  topic,
  category,
  topicCategory,
  secondaryTopics,
  latestArticles,
  location,
  states,
  modalQuestions,
  categoryDetails
}) => {
  const { costGuideData } = article;
  const { image, title, text, list } = costGuideData ? costGuideData : ({} as any);
  const {
    hero,
    metaTitle,
    metaDesc,
    heroType,
    body,
    redirectTo,
    lastModified,
    createdDate,
    author,
    readingTime,
    youtubeUrl,
    snippet,
    prevArticle,
    nextArticle,
    faq
  } = article;
  const isShowingCostGuide = heroType === 'costguide' && image && image.length > 0;
  const { person, personPlural, key } = category;
  const { id: categoryId, practiceParentId } = categoryDetails;
  const questions = modalQuestions[key];
  const [cookieValue, setCookie] = useState('');
  const [modalData, setModalData] = useState({
    postcodeValue: '',
    isOpen: false
  });
  const [isAppOnly, setIsAppOnly] = useState(false);
  const changeModalStatus = newStatus => {
    setModalData({ ...modalData, isOpen: newStatus });
  };

  const getSearchTitle = () => {
    const tradie = person ? person : 'tradie';
    return `Find ${addEnglishArticleWord(tradie)} professional the easy way`;
  };

  const seoMetrics = {
    h1: article.title,
    metaDescription: metaDesc,
    title: metaTitle,
    siteSection: 'Articles'
  };

  useSetupSeoMetrics(seoMetrics);

  useEffect(() => {
    const { cookie } = document;
    if (cookie.includes('isAppOnly=true')) {
      setIsAppOnly(true);
    }
    setCookie(cookie);
  }, []);

  const isModal =
    category &&
    categoryDetails &&
    key &&
    ![1, 91].includes(practiceParentId) &&
    !key.includes('ikea') &&
    !key.includes('bunnings') &&
    !location.search.includes('app_only=1') &&
    !isAppOnly;

  useEffect(() => {
    googleTagManager.dataLayerPush('category', topicCategory.key);
  }, [topicCategory]);

  const initialLocation = getCookieLocation(cookieValue);

  const onSubmitFn = (location, categoryKey = key) => {
    if (isModal) {
      setModalData({ postcodeValue: location, isOpen: true });
    } else {
      goToPage(location, categoryKey);
    }
  };

  const bannerData = isShowingCostGuide
    ? {
        onSubmit: (values, { setSubmitting }) => {
          const { location } = values;
          setTimeout(() => {
            onSubmitFn(location);
            setSubmitting(false);
          }, 500);
        },
        desktopContent: list.length > 0 ? list : text,
        subtitle: title,
        searchTitle: getSearchTitle(),
        title: article.title,
        initialLocation,
        onChangeCallback: values => onSubmitFn(values.value, key),
        image
      }
    : {};
  const breadcrumbsRequired = {
    article,
    category: topicCategory,
    topic
  };
  const breadcrumbsData = getArticleBreadcrumbs(breadcrumbsRequired);

  const articleSchema = generateArticleSchema({
    path: location.pathname,
    title: article.title,
    image: hero,
    category,
    topic,
    secondaryTopics,
    body,
    lastModified,
    createdDate,
    author,
    metaDesc,
    readingTime,
    breadcrumbsData,
    faq
  });

  const SuccessSection = () => {
    if (articleStatus !== ActionTypes.FETCH_ARTICLE_SUCCESS) {
      return null;
    }

    return (
      <>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDesc} />
          <meta name="name" content={article.title} />
          <meta property="og:title" content={article.title} />
          <meta property="og:image" content={hero} />
          <script type="application/ld+json">{`${JSON.stringify(articleSchema)}`}</script>
        </Helmet>
        {isShowingCostGuide ? (
          <CostGuideBanner {...bannerData} />
        ) : (
          <HeroJumbotron
            heroType={heroType}
            category={category}
            categoryPerson={person}
            categoryPersonPlural={personPlural}
            heroImage={hero}
            isModal={isModal}
            setModalData={setModalData}
          />
        )}
        <Breadcrumbs breadcrumbs={breadcrumbsData} />
        <Container pl={{ xs: 0, md: '20px' }} pr={{ xs: 0, md: '20px' }}>
          <ResponsiveCard>
            <Row alignItems={'stretch'}>
              <StyledCol xs={0} md={1}>
                <SocialShareBar />
              </StyledCol>
              <Col xs={12} md={11}>
                <HeadingTwo as="h1" m={0} align={'center'}>
                  {article.title}
                </HeadingTwo>
                <SmallText align={'center'}>
                  {lastModified && `Last Updated ${formatDate(lastModified)}`}
                  {lastModified && author && ' · '}
                  {author && `Written by ${author}`}
                  {(lastModified || author) && readingTime && ' · '}
                  {`${Math.ceil(readingTime)} min read`}
                </SmallText>
                <TopicLabels topic={topic} secondaryTopics={secondaryTopics} />
                <HrContainer />
                <Video autoplay={0} youtubeUrl={youtubeUrl} />
                <ArticleBody body={body} snippet={snippet} />
                <ArticleSuggestions prevArticle={prevArticle} nextArticle={nextArticle} />
                <SocialShareBarMobile />
                {faq && faq.length > 0 && <FAQ data={faq} />}
              </Col>
            </Row>
          </ResponsiveCard>
        </Container>
      </>
    );
  };

  return (
    <div>
      {redirectTo && <Redirect to={redirectTo} />}
      {articleStatus === ActionTypes.FETCH_ARTICLE_NOT_FOUND && <ArticleNotFound />}
      {articleStatus === ActionTypes.FETCH_ARTICLE_ERROR && <ArticleError />}
      <SuccessSection />
      <LatestArticles
        articles={latestArticles}
        topicName={topic.name}
        topicKey={topic.key}
        topicCategory={topic.categoryKey}
      />
      <CategoryList category={category} states={states} />
      <GetQuotesWidget category={category} categoryPersonPlural={category.personPlural} />
      {isModal && modalData.isOpen && (
        <Suspense fallback={false}>
          <GetQuotesModal
            questions={questions}
            initialLocation={modalData.postcodeValue || ''}
            categoryId={categoryId}
            category={category.key}
            categoryPlural={category.personPlural}
            skipLocationStep={true}
            open={modalData.isOpen}
            setOpen={changeModalStatus}
            trackEvent={trackEvent.bind(this)}
            pathName={location.pathname}
          />
        </Suspense>
      )}
    </div>
  );
};

Article.propTypes = {
  articleStatus: PropTypes.string,
  article: PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string,
    author: PropTypes.string,
    body: PropTypes.string,
    title: PropTypes.string,
    lastModified: PropTypes.string,
    createdData: PropTypes.string,
    youtubeUrl: PropTypes.string,
    prevArticle: PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string
    }),
    nextArticle: PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string
    })
  }),
  topic: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string
  }),
  category: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string
  }),
  topicCategory: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }),
  latestArticles: PropTypes.array,
  secondaryTopics: PropTypes.array,
  states: PropTypes.array
};

export default connect((state, props: any) => {
  const key = props.match.params.key;
  return {
    articleStatus: getArticleStatusByKey(state, key),
    article: getArticleByKey(state, key),
    latestArticles: getLatestTopicArticlesByArticleKey(state, key),
    topic: getTopicByArticleKey(state, key),
    secondaryTopics: getSecondaryTopicsByArticleKey(state, key),
    category: getCatgoryByArticleKey(state, key),
    topicCategory: getTopicCatgoryByArticleKey(state, key),
    states: getStates(state),
    modalQuestions: getModalQuestionsByCategory(state),
    categoryDetails: getCategoryDetails(state)
  };
})(Article);
